<template>
  <div class="flex">
    <!-- 左侧配置卡片列表 -->
    <div class="overflow-y-auto">
      <div class="flex flex-wrap gap-2">
        <div
          v-if="canAddConfig"
          class="flex shrink-0 items-center justify-center size-40 rounded-3xl bg-key cursor-pointer text-text-glass-default hover:text-text-glass-hover active:text-brand-blue2 transition-colors"
          @click="handleAddConfig"
        >
          <Icon icon="Function-Wireframe-Add11" class="size-18" />
        </div>

        <Card
          v-for="config in currentConfigs"
          :id="String(config.index)"
          :key="config.index"
          :name="config.name"
          :is-active="currentConfig?.index === config.index"
          :is-new="newlyAddedConfigId === config.index"
          :show-delete="!hasConfigSlots"
          :show-edit="!hasConfigSlots"
          @click="handleConfigSelect(String(config.index))"
          @upload="openUploadDialog"
          @download="openConfigListDialog"
          @delete="handleDeleteConfig"
          @edit="handleEditConfig"
        />
      </div>
    </div>

    <!-- 确认上传弹窗 -->
    <Dialog
      v-model:open="isUploadDialogOpen"
      :title="$t('home.config.uploadDialog.title')"
      content-class="w-90"
      :close-on-overlay-click="false"
    >
      <div class="flex items-center gap-2">
        <div class="whitespace-nowrap text-black-white">
          {{ $t('home.config.uploadDialog.deviceModelLabel') }}
        </div>
        <Input
          v-model="deviceModel"
          class="text-text-active placeholder:text-text-explanatory-default"
          :placeholder="$t('home.config.uploadDialog.deviceModelInput')"
          :disabled="true"
        />
      </div>
      <div class="flex items-center gap-2">
        <div class="whitespace-nowrap text-black-white">
          {{ $t('home.config.uploadDialog.configNameLabel') }}
        </div>
        <Input
          v-model="configName"
          class="text-text-active placeholder:text-text-explanatory-default"
          :placeholder="$t('home.config.uploadDialog.configNameInput')"
        />
      </div>
      <template #footer>
        <div class="flex gap-2 justify-end">
          <Button color="black" @click="closeUploadDialog">{{ $t('common.cancel') }}</Button>
          <Button color="text-b-w" @click="handleUploadConfig">{{ $t('common.confirm') }}</Button>
        </div>
      </template>
    </Dialog>

    <!-- 上传文件进度弹窗 -->
    <Dialog
      v-model:open="isUploading"
      description=""
      :close-on-overlay-click="false"
      content-class="w-145 p-6 gap-6"
    >
      <rocketProgress v-model="uploadProgress" />
      <div class="text-s-medium w-full text-center text-text-glass-default">
        {{ $t('home.config.uploadingDialog.text') }}
      </div>
    </Dialog>

    <!-- 配置文件列表弹窗 -->
    <Dialog
      v-model:open="isConfigListDialogOpen"
      :title="$t('home.config.configListDialog.title')"
      content-class="w-98"
    >
      <div class="flex flex-col gap-2 max-h-47 w-full overflow-auto pb-2">
        <div v-for="(item, i) in configList" :key="i" class="flex w-full items-center gap-2">
          <div
            class="px-3 flex items-center gap-2 w-82 h-8 cursor-pointer rounded-full transition-colors text-s-medium text-text-glass-default hover:text-text-glass-hover"
            :class="configItem?.id === item.id ? 'bg-key' : 'bg-neutral-light-default'"
            @click="selectConfigItem(item)"
          >
            <Icon :icon="deviceIcon" class="size-5 shrink-0" />

            <span class="shrink-0 whitespace-nowrap">
              {{ item.configType }}
            </span>

            <span class="flex-1 min-w-0 truncate">
              {{ item.name }}
            </span>

            <span class="shrink-0 whitespace-nowrap">
              {{ formatDateSafe(item.uploadTime, 'YYYY/MM/DD') }}
            </span>
          </div>
          <Popover v-slot="{ close }" class="z-1000">
            <PopoverTrigger>
              <Icon
                icon="Function-Wireframe-Delete"
                class="size-5.5 cursor-pointer text-s-medium transition-colors text-black-white hover:text-text-glass-hover"
              />
            </PopoverTrigger>
            <PopoverContent side="right">
              <p>{{ $t('home.config.configListDialog.deleteConfirmText') }}</p>
              <template #footer>
                <Button size="xs" color="black" @click.stop="close">{{
                  $t('common.cancel')
                }}</Button>
                <Button
                  size="xs"
                  color="text-b-b"
                  @click.stop="openDelLoadDialog(item, $event, close)"
                  >{{ $t('common.delete') }}</Button
                >
              </template>
            </PopoverContent>
          </Popover>
        </div>
      </div>
      <template #footer>
        <div class="flex gap-2 justify-end">
          <Button color="text-b-w" @click="closeConfigListDialog">{{ $t('common.cancel') }}</Button>
          <Button :disabled="!configItem?.id" color="text-b-w" @click="handleDownloadConfig">
            {{ $t('home.config.configListDialog.footerButton') }}
          </Button>
        </div>
      </template>
    </Dialog>

    <!-- 下载文件弹窗 -->
    <Dialog v-model:open="isDownloading" :close-on-overlay-click="false">
      <rocketProgress v-model="downloadProgress" />
      <div class="text-s-medium w-full text-center text-text-glass-default">
        {{ $t('home.config.downloadingDialog.text') }}
      </div>
    </Dialog>

    <!-- 删除确认弹窗 -->
    <Dialog
      v-model:open="isDeleteDialogOpen"
      :title="$t('home.config.deleteDialog.title')"
      content-class="w-90"
    >
      <div class="py-2 text-m-medium text-text-glass-default">
        {{
          String(operatingConfigId) === String(currentConfig?.index)
            ? $t('home.config.deleteDialog.confirmText2')
            : $t('home.config.deleteDialog.confirmText')
        }}
      </div>
      <template #footer>
        <div class="flex gap-2 justify-end">
          <Button color="black" @click="isDeleteDialogOpen = false">{{
            $t('common.cancel')
          }}</Button>
          <Button color="text-b-w" @click="confirmDelete">{{ $t('common.delete') }}</Button>
        </div>
      </template>
    </Dialog>

    <!-- 重命名弹窗 -->
    <Dialog
      v-model:open="isRenameDialogOpen"
      :title="$t('home.config.renameDialog.title')"
      content-class="w-90 glass-s60"
    >
      <div class="py-2">
        <Input
          v-model="newName"
          class="text-black-white text-text-glass-default"
          :placeholder="$t('home.config.renameDialog.input')"
          @keydown.enter="confirmRename"
        />
        <div v-if="isNameOverLimit" class="mt-1 text-xs text-red-500">
          {{ $t('home.config.renameDialog.overLimitTip') }}
        </div>
      </div>
      <template #footer>
        <div class="flex gap-2 justify-end">
          <Button color="text-b-w" @click="isRenameDialogOpen = false">{{
            $t('common.cancel')
          }}</Button>
          <Button
            color="text-b-w"
            :disabled="isNameOverLimit || isNameEmpty"
            @click="confirmRename"
            >{{ $t('common.confirm') }}</Button
          >
        </div>
      </template>
    </Dialog>
  </div>
</template>

<script lang="ts" setup>
  import { computed, onMounted, onUnmounted, ref } from 'vue'
  import { storeToRefs } from 'pinia'
  import {
    Icon,
    Dialog,
    Button,
    Input,
    Popover,
    PopoverContent,
    PopoverTrigger,
  } from '@rapoo/element'
  import { type ConfigCapability, convertStrToASCII } from '@rapoo/device-sdk'
  import { useConfigName, useNewConfigDefaultName } from '@/composables/useConfigName'
  import { useConfigurationStore, useDeviceStore } from '@/stores'
  import Card from './Card.vue'
  import rocketProgress from '@/components/rocketProgress/index.vue'
  import { useConfigCloud } from './composables/useConfigCloud'
  import { formatDateSafe } from '@/utils/date'
  import { useAdminOnly } from '@/composables/useAdminOnly/index.ts'

  // 定义 Props
  defineProps<{
    activeTab?: string
  }>()

  onMounted(() => {
    configStore.refreshProfiles()
  })

  const configStore = useConfigurationStore()
  const { configs, currentConfig } = storeToRefs(configStore)
  const currentConfigs = computed(() =>
    authConfigs.value.map((item) => ({
      ...item,
      name: useConfigName(item, currentDevice.value),
    })),
  )

  const deviceStore = useDeviceStore()
  const { currentDevice } = storeToRefs(deviceStore)

  // 弹窗状态
  const isDeleteDialogOpen = ref(false)
  const isRenameDialogOpen = ref(false)
  const operatingConfigId = ref<string>('')
  const newName = ref('')

  /** 刚新增的配置 index，用于展示 NEW 标识 */
  const newlyAddedConfigId = ref<number | null>(null)
  let newlyAddedTimer: ReturnType<typeof setTimeout> | null = null

  const markConfigAsNew = (index: number) => {
    newlyAddedConfigId.value = index
    if (newlyAddedTimer) clearTimeout(newlyAddedTimer)
    newlyAddedTimer = setTimeout(() => {
      newlyAddedConfigId.value = null
      newlyAddedTimer = null
    }, 3000)
  }

  onUnmounted(() => {
    if (newlyAddedTimer) clearTimeout(newlyAddedTimer)
  })

  /** 获取 config 能力配置 */
  const configCap = computed(() => {
    return currentDevice.value?.manifest.capabilities.find((item) => item.type === 'config') as
      | ConfigCapability
      | undefined
  })

  const maxByteLen = computed(() => {
    return configCap.value?.config.maxNameLen ?? Infinity
  })

  /** 输入内容字节长度是否超过限制 */
  const isNameOverLimit = computed(() => {
    const max = maxByteLen.value
    if (max === Infinity) return false
    return convertStrToASCII(newName.value).length > max
  })

  /** 是否有配置槽位 */
  const hasConfigSlots = computed(() => !!configCap.value?.config.configSlots?.length)

  // ------------------- 权限管理 -------------------
  /**
   * 是否可以继续添加新配置。
   * - 有 configSlots：空名视为未占用（与固件按空名找槽一致）
   * - 无 configSlots（如 GCOME）：列表项即占用，空名「未命名」也算占用
   */
  const canAddConfig = computed(() => {
    if (hasConfigSlots.value) {
      const occupiedCount = authConfigs.value.filter((config) => config.name.trim() !== '').length
      return occupiedCount < maxConfigNum.value
    }
    return authConfigs.value.length < maxConfigNum.value
  })
  /** 用户有权限的最大配置数 */
  const maxConfigNum = computed(() => {
    const { configRoleMaxCount } = useAdminOnly(currentDevice.value)
    return configRoleMaxCount.value
  })
  /** 用户有权限的配置，有配置槽位时根据角色过滤，无则全部显示 */
  const authConfigs = computed(() => {
    const { configRoleVisible } = useAdminOnly(currentDevice.value)
    return configRoleVisible.value
  })
  /** 输入内容为空或仅空格 */
  const isNameEmpty = computed(() => newName.value.trim() === '')

  // 云端配置逻辑解构
  const {
    // 基础数据
    configName,
    deviceModel,
    // 上传模块
    isUploadDialogOpen,
    uploadProgress,
    isUploading,
    openUploadDialog,
    closeUploadDialog,
    handleUploadConfig,
    // 列表模块
    isConfigListDialogOpen,
    configList, // 同步拼写修正
    deviceIcon,
    openConfigListDialog,
    closeConfigListDialog,
    // 下载模块
    configItem,
    isDownloading,
    downloadProgress,
    selectConfigItem,
    handleDownloadConfig,
    // 删除模块
    openDelLoadDialog,
  } = useConfigCloud()

  /**
   * 处理配置选择
   * @param id 配置ID (string 类型的 index)
   */
  const handleConfigSelect = (id: string) => {
    configStore.setActiveProfile(Number(id))
  }

  /**
   * 处理删除配置
   * @param id 配置ID
   */
  const handleDeleteConfig = (id: string) => {
    operatingConfigId.value = id
    isDeleteDialogOpen.value = true
  }

  /**
   * 确认删除配置
   */
  const confirmDelete = async () => {
    if (!operatingConfigId.value) return
    await configStore.removeProfile(Number(operatingConfigId.value))
    isDeleteDialogOpen.value = false
  }

  /**
   * 处理编辑配置（重命名）
   * @param id 配置ID
   */
  const handleEditConfig = (id: string) => {
    const config = configs.value.find((c) => String(c.index) === id)
    if (!config) return

    operatingConfigId.value = id
    // 使用 useConfigName 获取翻译后的显示名，避免编辑框显示原始 i18n key
    newName.value = useConfigName(config, currentDevice.value)
    isRenameDialogOpen.value = true
  }

  /**
   * 确认重命名
   */
  const confirmRename = async () => {
    const trimedName = newName.value.trim()
    if (!operatingConfigId.value || !trimedName || isNameOverLimit.value) return

    const config = configs.value.find((c) => String(c.index) === operatingConfigId.value)
    if (config) {
      await configStore.updateProfileName(config, trimedName)
      isRenameDialogOpen.value = false
    }
  }

  /**
   * 处理添加新配置
   */
  const handleAddConfig = async () => {
    const { name, configIndex } = useNewConfigDefaultName(currentDevice.value, authConfigs.value)
    const createdIndex = await configStore.addProfile(name, configIndex)
    if (createdIndex != null) {
      markConfigAsNew(createdIndex)
    } else if (configIndex != null) {
      markConfigAsNew(configIndex)
    }
  }
</script>

<style lang="scss" scoped>
  /* 自定义滚动条样式 */
  ::-webkit-scrollbar {
    width: 6px;
  }

  ::-webkit-scrollbar-track {
    background: transparent;
  }

  ::-webkit-scrollbar-thumb {
    background-color: var(--color-neutral-light-line);
    border-radius: 3px;

    &:hover {
      background-color: var(--color-neutral-dark-placeholder);
    }
  }
</style>
